import { Controller, type FieldPath, type FieldValues } from "react-hook-form"
import { isPlainObject } from "remeda"
import { useFieldRoleConf } from "../../../context"
import {
  SearchAutocomplete,
  type SearchAutocompleteProps,
  type SearchOption,
  getOptionValue,
} from "./SearchAutocomplete"

export type ValueFormat = "optionObject" | "optionValue"
export interface FormSearchFieldProps<
  Option,
  TFieldValues extends FieldValues = FieldValues,
> extends Omit<
    SearchAutocompleteProps<Option>,
    "error" | "helperText" | "defaultValue"
  > {
  name: FieldPath<TFieldValues>
  /** Defines the format used in value & onChange */
  valueFormat?: ValueFormat
  defaultValue?: SearchOption<Option>[] | Option | string
}

/**
 * Search: basically a select with autocomplete
 * NB: The list is not async (but could be ? What if options is a Promise ?)
 */
export const FormSearchField = <
  Option extends string | number,
  TFieldValues extends FieldValues = FieldValues,
>({
  name,
  multiple = false,
  valueFormat = "optionObject",
  defaultValue,
  ...searchAutocompleteProps
}: FormSearchFieldProps<Option, TFieldValues>) => {
  const fieldConf = useFieldRoleConf(name)
  return (
    <Controller
      name={name}
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      defaultValue={defaultValue as any}
      render={({ field, fieldState: { error } }) => (
        <SearchAutocomplete
          id={name}
          {...fieldConf}
          ref={field.ref}
          multiple={multiple}
          value={field.value || defaultValue}
          onChange={(_event, newValue) => {
            if (Array.isArray(newValue)) {
              const arrayValue = (
                newValue.length > 0 ? newValue : (defaultValue ?? [])
              ) as SearchOption<Option>[]
              field.onChange(
                arrayValue?.map((newValueEl) =>
                  getFieldValue(newValueEl, valueFormat),
                ),
              )
            } else {
              field.onChange(getFieldValue(newValue || defaultValue, valueFormat))
            }
          }}
          disableClearable={multiple ? !field.value?.length : !field.value}
          error={!!error}
          helperText={error?.message}
          {...searchAutocompleteProps}
        />
      )}
    />
  )
}

const getFieldValue = <T,>(option: T, valueFormat: ValueFormat) =>
  valueFormat === "optionObject" && isPlainObject(option)
    ? option
    : getOptionValue(option)
